import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Divider,
  Chip,
  Stack,
  Grid,
  CircularProgress,
  TextField,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  Group as GroupIcon,
  Delete as DeleteIcon,
  VpnKey as VpnKeyIcon,
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  AccessTime as AccessTimeIcon,
  Apps as AppsIcon,
  BlockOutlined as BlockIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import apiService from '../../services/api';
import { getCustomerIdFromSubdomain, isCustomerSubdomain } from '../../utils/constants';
import { isPasswordValid, getPasswordErrorMessage } from '../../utils/passwordValidation';
import PasswordStrengthIndicator from '../common/PasswordStrengthIndicator';
import RevokeAccessDialog from '../common/RevokeAccessDialog';

const UserDetails = ({ user, onUpdate, onDelete, onClose, onRevokeComplete }) => {
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: '',
    temporary: false
  });
  const [changingPassword, setChangingPassword] = useState(false);

  // MFA state
  const [mfaStatus, setMfaStatus] = useState(null); // 'configured' | 'enforced' | 'none' | null
  const [isFederated, setIsFederated] = useState(false);
  const [loadingMfa, setLoadingMfa] = useState(false);
  const [togglingMfa, setTogglingMfa] = useState(false);

  // Fetch MFA status for this user on mount (customer realms only)
  useEffect(() => {
    if (!isCustomerSubdomain() || !user?.id) return;
    const fetchMfaStatus = async () => {
      setLoadingMfa(true);
      try {
        const response = await apiService.getUsersMFAStatus();
        if (response.success && Array.isArray(response.data)) {
          const found = response.data.find(u => u.id === user.id);
          if (found) {
            setMfaStatus(found.mfaStatus);
            setIsFederated(!!found.federated);
          }
        }
      } catch (err) {
        console.error('Error fetching MFA status:', err);
      } finally {
        setLoadingMfa(false);
      }
    };
    fetchMfaStatus();
  }, [user?.id]);

  const handleMfaToggle = async () => {
    if (!user?.id) return;
    setTogglingMfa(true);
    try {
      if (mfaStatus === 'enforced' || mfaStatus === 'configured') {
        const response = await apiService.removeMFAEnforcement([user.id]);
        if (response.success) {
          setMfaStatus('none');
          toast.success(
            mfaStatus === 'configured'
              ? 'MFA removed for this user'
              : 'MFA enforcement removed for this user'
          );
        }
      } else {
        const response = await apiService.enforceMFA([user.id]);
        if (response.success) {
          setMfaStatus('enforced');
          toast.success('MFA enforced for this user');
        }
      }
    } catch (err) {
      toast.error(err.message || 'Failed to update MFA status');
    } finally {
      setTogglingMfa(false);
    }
  };

  const directAssignments = (user.assignments || []).filter((a) => a.assignmentType === 'direct');
  const directAppAssignments = (user.applicationAssignments || []).filter((a) => a.assignmentType === 'direct');
  const hasDirectAccess = directAssignments.length > 0 || directAppAssignments.length > 0;

  const handleRevokeAccess = async () => {
    setIsRevoking(true);
    const customerId = getCustomerIdFromSubdomain();
    let successCount = 0;
    let failCount = 0;
    const totalCount = directAssignments.length + directAppAssignments.length;

    for (const assignment of directAssignments) {
      try {
        await apiService.deletePermissionSetAssignment(assignment.id);
        successCount++;
      } catch (err) {
        console.error('Failed to revoke assignment:', err);
        failCount++;
      }
    }

    for (const appAssignment of directAppAssignments) {
      try {
        await apiService.removeUserFromApplication(customerId, appAssignment.applicationId, user.username);
        successCount++;
      } catch (err) {
        console.error('Failed to revoke app assignment:', err);
        failCount++;
      }
    }

    if (failCount === 0) {
      toast.success(`Successfully revoked ${successCount} of ${totalCount} assignments`);
    } else {
      toast.warning(`Revoked ${successCount} of ${totalCount} assignments (${failCount} failed)`);
    }

    setIsRevoking(false);
    setShowRevokeDialog(false);
    if (onRevokeComplete) onRevokeComplete();
  };

  const handleDelete = () => {
    onDelete(user.username);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (!isPasswordValid(passwordForm.newPassword)) {
      toast.error(getPasswordErrorMessage(passwordForm.newPassword));
      return;
    }

    setChangingPassword(true);
    try {
      const response = await apiService.changeUserPassword(
        user.username,
        passwordForm.newPassword,
        passwordForm.temporary
      );

      if (response.success) {
        toast.success(`Password ${passwordForm.temporary ? 'set' : 'changed'} successfully`);
        setShowPasswordDialog(false);
        setPasswordForm({
          newPassword: '',
          confirmPassword: '',
          temporary: false
        });
      }
    } catch (err) {
      toast.error(err.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#FFFFFF',
      borderRadius: '6px',
      fontFamily: 'Metropolis, sans-serif',
      '& fieldset': {
        borderColor: '#E5E5E5',
      },
      '&:hover fieldset': {
        borderColor: '#4698D3',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#4698D3',
      },
    },
    '& .MuiInputLabel-root': {
      fontFamily: 'Metropolis, sans-serif',
      color: '#666666',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#4698D3',
    },
    '& .MuiFormHelperText-root': {
      fontFamily: 'Metropolis, sans-serif',
      color: '#999999',
    },
  };

  const containerVariants = {
    initial: {},
    animate: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
  };
  const itemVariants = {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } },
  };

  return (
    <motion.div variants={containerVariants} initial="initial" animate="animate">
      {/* Header */}
      <motion.div variants={itemVariants}>
      <Box
        sx={{
          p: 3,
          borderBottom: '1px solid #E5E5E5',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              backgroundColor: user.enabled === false ? '#BDBDBD' : '#4698D3',
              width: 48,
              height: 48,
            }}
          >
            <PersonIcon sx={{ fontSize: 24 }} />
          </Avatar>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 600,
                  fontSize: '1.5rem',
                  color: '#253E66',
                }}
              >
                {user.username}
              </Typography>
              {user.enabled === false && (
                <Chip
                  label="Disabled"
                  size="small"
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    backgroundColor: 'rgba(231, 76, 60, 0.1)',
                    border: '1px solid rgba(231, 76, 60, 0.2)',
                    color: '#E74C3C',
                  }}
                />
              )}
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: '#999999',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 500,
              }}
            >
              User Details
            </Typography>
          </Box>
        </Box>
      </Box>
      </motion.div>

      {/* Content */}
      <Box sx={{ p: 3 }}>
        {/* User Information */}
        <motion.div variants={itemVariants}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '8px',
                  backgroundColor: '#F0F4F8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <EmailIcon sx={{ color: '#4698D3', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#999999',
                    fontFamily: 'Metropolis, sans-serif',
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                    letterSpacing: '0.5px',
                  }}
                >
                  Email
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    color: '#253E66',
                    fontFamily: 'Metropolis, sans-serif',
                  }}
                >
                  {user.email}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '8px',
                  backgroundColor: '#F0F4F8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <BadgeIcon sx={{ color: '#D64794', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#999999',
                    fontFamily: 'Metropolis, sans-serif',
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                    letterSpacing: '0.5px',
                  }}
                >
                  Name
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    color: '#253E66',
                    fontFamily: 'Metropolis, sans-serif',
                  }}
                >
                  {user.firstName} {user.lastName}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
        </motion.div>

        <Divider sx={{ my: 3, borderColor: '#E5E5E5' }} />

        {/* Group Memberships */}
        <motion.div variants={itemVariants}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <GroupIcon sx={{ color: '#4698D3', fontSize: 20 }} />
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                color: '#253E66',
              }}
            >
              Group Memberships
            </Typography>
            {user.isLoadingGroups && (
              <CircularProgress size={16} sx={{ color: '#4698D3' }} />
            )}
          </Box>

          <Box sx={{ pl: 0.5 }}>
            {user.isLoadingGroups ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                <CircularProgress size={16} sx={{ color: '#4698D3' }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: '#999999',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 500,
                  }}
                >
                  Loading group memberships...
                </Typography>
              </Box>
            ) : (
              <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}>
              {user.groups && user.groups.length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                  {user.groups.map((group, index) => (
                    <Chip
                      key={index}
                      label={group}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(70, 152, 211, 0.1)',
                        border: '1px solid rgba(70, 152, 211, 0.2)',
                        color: '#4698D3',
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 500,
                      }}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    color: '#999999',
                    fontFamily: 'Metropolis, sans-serif',
                    fontStyle: 'italic',
                    fontWeight: 500,
                  }}
                >
                  User is not a member of any groups
                </Typography>
              )}
              </motion.div>
            )}
          </Box>
        </Box>

        </motion.div>

        <Divider sx={{ my: 3, borderColor: '#E5E5E5' }} />

        {/* Effective Assignments */}
        <motion.div variants={itemVariants}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <AssignmentIcon sx={{ color: '#7B1FA2', fontSize: 20 }} />
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                color: '#253E66',
              }}
            >
              Effective Assignments
            </Typography>
            {user.isLoadingAssignments && (
              <CircularProgress size={16} sx={{ color: '#7B1FA2' }} />
            )}
          </Box>

          <Box sx={{ pl: 0.5 }}>
            {user.isLoadingAssignments ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                <CircularProgress size={16} sx={{ color: '#7B1FA2' }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: '#999999',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 500,
                  }}
                >
                  Loading assignments...
                </Typography>
              </Box>
            ) : (
              <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}>
              {user.assignments && user.assignments.length > 0 ? (
              <TableContainer sx={{ backgroundColor: '#F9FBFD', borderRadius: '8px', border: '1px solid #E5E5E5' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Permission Set', 'Account', 'Access Type', 'JIT'].map((header) => (
                        <TableCell
                          key={header}
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 600,
                            color: '#253E66',
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            borderBottom: '2px solid #E5E5E5',
                          }}
                        >
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {user.assignments.map((assignment) => (
                      <TableRow key={assignment.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 500,
                            color: '#253E66',
                            fontSize: '0.85rem',
                          }}
                        >
                          {assignment.permissionSetName}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            color: '#253E66',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'Metropolis, sans-serif',
                              fontWeight: 500,
                              color: '#253E66',
                              fontSize: '0.85rem',
                              lineHeight: 1.3,
                            }}
                          >
                            {assignment.accountName || assignment.accountId}
                          </Typography>
                          {assignment.accountName && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontFamily: 'Metropolis, sans-serif',
                                color: '#999999',
                                fontSize: '0.7rem',
                              }}
                            >
                              ({assignment.accountId})
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {assignment.assignmentType === 'direct' ? (
                            <Chip
                              label="Direct"
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(70, 152, 211, 0.1)',
                                border: '1px solid rgba(70, 152, 211, 0.2)',
                                color: '#4698D3',
                                fontFamily: 'Metropolis, sans-serif',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                              }}
                            />
                          ) : (
                            <Chip
                              label={`via ${assignment.groupName}`}
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(214, 71, 148, 0.1)',
                                border: '1px solid rgba(214, 71, 148, 0.2)',
                                color: '#D64794',
                                fontFamily: 'Metropolis, sans-serif',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {assignment.isJIT ? (
                            <Chip
                              icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
                              label={assignment.jitExpiresAt ? new Date(assignment.jitExpiresAt).toLocaleString() : 'JIT'}
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(243, 156, 18, 0.1)',
                                border: '1px solid rgba(243, 156, 18, 0.2)',
                                color: '#F39C12',
                                fontFamily: 'Metropolis, sans-serif',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                '& .MuiChip-icon': { color: '#F39C12' },
                              }}
                            />
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#999999',
                                fontFamily: 'Metropolis, sans-serif',
                              }}
                            >
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: '#999999',
                  fontFamily: 'Metropolis, sans-serif',
                  fontStyle: 'italic',
                  fontWeight: 500,
                }}
              >
                No permission set assignments found for this user
              </Typography>
              )}
              </motion.div>
            )}
          </Box>
        </Box>

        </motion.div>

        {/* Application Assignments Section */}
        <motion.div variants={itemVariants}>
        <Box sx={{ mb: 3, mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <AppsIcon sx={{ color: '#8E44AD', fontSize: 20 }} />
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                color: '#253E66',
              }}
            >
              Application Assignments
            </Typography>
            {user.isLoadingAssignments && (
              <CircularProgress size={16} sx={{ color: '#8E44AD' }} />
            )}
          </Box>

          <Box sx={{ pl: 0.5 }}>
            {user.isLoadingAssignments ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                <CircularProgress size={16} sx={{ color: '#8E44AD' }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: '#999999',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 500,
                  }}
                >
                  Loading application assignments...
                </Typography>
              </Box>
            ) : (
              <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}>
              {user.applicationAssignments && user.applicationAssignments.length > 0 ? (
              <TableContainer sx={{ backgroundColor: '#F9FBFD', borderRadius: '8px', border: '1px solid #E5E5E5' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Application Name', 'Access Type'].map((header) => (
                        <TableCell
                          key={header}
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 600,
                            color: '#253E66',
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            borderBottom: '2px solid #E5E5E5',
                          }}
                        >
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {user.applicationAssignments.map((appAssignment) => (
                      <TableRow key={appAssignment.applicationId} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 500,
                            color: '#253E66',
                            fontSize: '0.85rem',
                          }}
                        >
                          {appAssignment.applicationName || appAssignment.clientId}
                        </TableCell>
                        <TableCell>
                          {appAssignment.assignmentType === 'direct' ? (
                            <Chip
                              label="Direct"
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(142, 68, 173, 0.1)',
                                border: '1px solid rgba(142, 68, 173, 0.2)',
                                color: '#8E44AD',
                                fontFamily: 'Metropolis, sans-serif',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                              }}
                            />
                          ) : (
                            <Chip
                              label={`via ${appAssignment.groupName}`}
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(214, 71, 148, 0.1)',
                                border: '1px solid rgba(214, 71, 148, 0.2)',
                                color: '#D64794',
                                fontFamily: 'Metropolis, sans-serif',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: '#999999',
                  fontFamily: 'Metropolis, sans-serif',
                  fontStyle: 'italic',
                  fontWeight: 500,
                }}
              >
                No application assignments found for this user
              </Typography>
              )}
              </motion.div>
            )}
          </Box>
        </Box>

        </motion.div>

        {/* MFA Status - Customer realms only */}
        {isCustomerSubdomain() && (
          <motion.div variants={itemVariants}>
            <Divider sx={{ my: 3, borderColor: '#E5E5E5' }} />
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <SecurityIcon sx={{ color: '#8E44AD', fontSize: 20 }} />
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    color: '#253E66',
                  }}
                >
                  Multi-Factor Authentication
                </Typography>
                {loadingMfa && <CircularProgress size={16} sx={{ color: '#8E44AD' }} />}
              </Box>

              {loadingMfa ? (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: '#F9FBFD',
                  borderRadius: '8px',
                  border: '1px solid #E5E5E5',
                  px: 2,
                  py: 1.5,
                }}>
                  <CircularProgress size={16} sx={{ color: '#8E44AD' }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#999999',
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 500,
                    }}
                  >
                    Loading MFA status...
                  </Typography>
                </Box>
              ) : (
              <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: '#F9FBFD',
                  borderRadius: '8px',
                  border: '1px solid #E5E5E5',
                  px: 2,
                  py: 1.5,
                  opacity: isFederated ? 0.6 : 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {isFederated ? (
                    <Chip
                      label="IdP-Linked"
                      size="small"
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        backgroundColor: '#EBF5FB',
                        color: '#2980B9',
                        border: '1px solid #2980B920',
                      }}
                    />
                  ) : (
                    <Chip
                      label={
                        mfaStatus === 'configured' ? 'Configured' :
                        mfaStatus === 'enforced' ? 'Pending Setup' :
                        'Not Enforced'
                      }
                      size="small"
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        backgroundColor:
                          mfaStatus === 'configured' ? '#F0F9F4' :
                          mfaStatus === 'enforced' ? '#FEF9E7' :
                          '#F5F5F5',
                        color:
                          mfaStatus === 'configured' ? '#27AE60' :
                          mfaStatus === 'enforced' ? '#F39C12' :
                          '#999999',
                        border: `1px solid ${
                          mfaStatus === 'configured' ? '#27AE6020' :
                          mfaStatus === 'enforced' ? '#F39C1220' :
                          '#E5E5E5'
                        }`,
                      }}
                    />
                  )}
                  <Typography
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontSize: '0.8rem',
                      color: '#666666',
                    }}
                  >
                    {isFederated
                      ? 'MFA is managed by the external identity provider'
                      : mfaStatus === 'configured'
                      ? 'User has configured an authenticator app'
                      : mfaStatus === 'enforced'
                      ? 'User must set up MFA on next login'
                      : 'MFA is not required for this user'}
                  </Typography>
                </Box>
                <Switch
                  size="small"
                  checked={!isFederated && (mfaStatus === 'enforced' || mfaStatus === 'configured')}
                  onChange={handleMfaToggle}
                  disabled={isFederated || togglingMfa}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#8E44AD' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#8E44AD' },
                  }}
                />
                {togglingMfa && <CircularProgress size={16} sx={{ color: '#8E44AD', ml: 1 }} />}
              </Box>
              </motion.div>
              )}
            </Box>
          </motion.div>
        )}

        <Divider sx={{ my: 3, borderColor: '#E5E5E5' }} />

        {/* Action Buttons */}
        <motion.div variants={itemVariants}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<VpnKeyIcon />}
            onClick={() => setShowPasswordDialog(true)}
            sx={{
              color: '#F39C12',
              borderColor: 'rgba(243, 156, 18, 0.3)',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#F39C12',
                backgroundColor: 'rgba(243, 156, 18, 0.05)',
              },
            }}
          >
            Change Password
          </Button>

          <Button
            variant="outlined"
            startIcon={<BlockIcon />}
            onClick={() => setShowRevokeDialog(true)}
            disabled={!hasDirectAccess}
            sx={{
              color: '#E74C3C',
              borderColor: 'rgba(231, 76, 60, 0.3)',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#E74C3C',
                backgroundColor: 'rgba(231, 76, 60, 0.05)',
              },
              '&.Mui-disabled': {
                color: '#BDBDBD',
                borderColor: '#E5E5E5',
              },
            }}
          >
            Revoke Access
          </Button>

          <Button
            variant="contained"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            sx={{
              backgroundColor: '#E74C3C',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: '#C0392B',
              },
            }}
          >
            Delete User
          </Button>
        </Box>
        </motion.div>
      </Box>

      {/* Password Change Dialog */}
      <Dialog
        open={showPasswordDialog}
        onClose={() => {
          setShowPasswordDialog(false);
          setPasswordForm({
            newPassword: '',
            confirmPassword: '',
            temporary: false
          });
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            backgroundColor: '#FFFFFF',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#F39C12',
            color: '#FFFFFF',
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            pr: 6,
          }}
        >
          <VpnKeyIcon />
          Change Password for {user.username}
          <IconButton
            onClick={() => {
              setShowPasswordDialog(false);
              setPasswordForm({
                newPassword: '',
                confirmPassword: '',
                temporary: false
              });
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Alert
            severity="info"
            sx={{
              mb: 3,
              mt: 1,
              backgroundColor: 'rgba(70, 152, 211, 0.1)',
              border: '1px solid rgba(70, 152, 211, 0.2)',
              color: '#253E66',
              fontFamily: 'Metropolis, sans-serif',
              '& .MuiAlert-icon': { color: '#4698D3' },
            }}
          >
            Set a new password for this user. The user will be able to use this password to log in.
          </Alert>

          <Box component="form" onSubmit={handlePasswordChange}>
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="password"
                  label="New Password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  required
                  disabled={changingPassword}
                  inputProps={{ minLength: 8 }}
                  size="small"
                  sx={inputStyles}
                />
                <PasswordStrengthIndicator password={passwordForm.newPassword} />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="password"
                  label="Confirm New Password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  required
                  disabled={changingPassword}
                  error={passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword}
                  helperText={
                    passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword
                      ? 'Passwords do not match'
                      : ''
                  }
                  size="small"
                  sx={{
                    ...inputStyles,
                    '& .MuiFormHelperText-root': {
                      color: passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword
                        ? '#E74C3C'
                        : '#999999',
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={passwordForm.temporary}
                      onChange={(e) => setPasswordForm({ ...passwordForm, temporary: e.target.checked })}
                      disabled={changingPassword}
                      sx={{
                        color: '#BDBDBD',
                        '&.Mui-checked': { color: '#F39C12' },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#253E66',
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: 500,
                        }}
                      >
                        Temporary Password
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#999999',
                          fontFamily: 'Metropolis, sans-serif',
                        }}
                      >
                        User will be required to change password on next login
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0, gap: 2 }}>
          <Button
            onClick={() => {
              setShowPasswordDialog(false);
              setPasswordForm({
                newPassword: '',
                confirmPassword: '',
                temporary: false
              });
            }}
            disabled={changingPassword}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              color: '#666666',
              borderColor: '#E5E5E5',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#BDBDBD',
                backgroundColor: '#F9FBFD',
              },
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handlePasswordChange}
            disabled={
              changingPassword ||
              !passwordForm.newPassword ||
              !passwordForm.confirmPassword ||
              passwordForm.newPassword !== passwordForm.confirmPassword ||
              !isPasswordValid(passwordForm.newPassword)
            }
            variant="contained"
            sx={{
              backgroundColor: '#F39C12',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: '#E67E22',
              },
              '&:disabled': {
                backgroundColor: '#E5E5E5',
                color: '#BDBDBD',
              },
            }}
            startIcon={changingPassword ? <CircularProgress size={16} color="inherit" /> : <VpnKeyIcon />}
          >
            {changingPassword ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke Access Dialog */}
      <RevokeAccessDialog
        open={showRevokeDialog}
        onClose={() => setShowRevokeDialog(false)}
        onConfirm={handleRevokeAccess}
        principalType="user"
        principalName={user.username}
        assignments={user.assignments || []}
        applicationAssignments={user.applicationAssignments || []}
        isRevoking={isRevoking}
      />
    </motion.div>
  );
};

export default UserDetails;
